import {
  AlertDialogCloseButton,
  AlertDialogHeader,
  Box,
  Button,
  Checkbox,
  Flex,
  FormControl,
  FormLabel,
  Grid,
  IconButton,
  Image,
  Input,
  Modal,
  ModalContent,
  ModalOverlay,
  Stack,
  Text,
  Tooltip,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import { ErrorMessage } from "@hookform/error-message";
import {
  useAddLabTestCategory,
  useEditLabTestCategory,
  useLabTestCategories,
} from "api/categories";
import { useLabTests, useTests } from "api/tests";
import Discount, { getDiscountValues } from "classes/discount";
import { add } from "date-fns";
import { getFormattedStringDate } from "helpers";
import { getBaseTimeStamp } from "helpers/timezoneHelper";
import { LocalFilePreview } from "modules/appointments/FilePreview";
import React, { useEffect, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { RiDeleteBin6Line } from "react-icons/ri";
import { weekdays, DiscountCard } from "../discounts/DiscountPage";

const defaultCategory = {
  tests: [],
  fee: 0,
};
export default function AddTestCategory({
  edit,
  onCancel,
  selectedCategory: category,
  setCategory,
}: {
  edit?: boolean | undefined;
  onCancel?: any;
  selectedCategory?: any;
  setCategory: any;
}) {
  const defaultDiscount = new Discount({});
  const labTestsQuery = useLabTests();
  const testsQuery = useTests();
  const labTestCategoriesQuery = useLabTestCategories();
  const [addTestCategory, addTestCategoryInfo] = useAddLabTestCategory();
  const [editTestCategory, editTestCategoryInfo] = useEditLabTestCategory();
  const [image, setImage] = useState<any>();
  const [initialDiscount, setInitialDiscount] = useState<any>({
    ...defaultDiscount,
    runsForever: true,
  });
  const [imageSrc, setImageSrc] = useState<any>();
  const [totalFee, setTotalFee] = useState<any>(0);
  const { handleSubmit, register, control, errors, setValue, watch } =
    useForm();
  let [selectedCategory, setSelectedCategory] = useState<any>(
    category ?? defaultCategory
  );
  const [availableTestsMap, setAvailableTestsMap] = useState<any>({});
  const toast = useToast();
  const uploadRef = useRef<any>(null);
  const imageModal = useDisclosure();
  const discountAlert = useDisclosure();
  const categoryFee = watch("fee");

  useEffect(() => {
    const labTestKeys = labTestCategoriesQuery.data?.map(
      (test: any) => test.parentId
    );
    if (labTestsQuery?.data) {
      labTestsQuery.data.forEach((test) => {
        // Here only normal tests are allowed to be added in category
        if (!labTestKeys?.includes(test.id) && test.labCollector === false) {
          availableTestsMap[test.id] = test;
        }
      });
      setAvailableTestsMap({ ...availableTestsMap });
    }
    // eslint-disable-next-line
  }, [labTestsQuery?.data, labTestCategoriesQuery?.data]);

  useEffect(() => {
    if (selectedCategory?.tests) {
      const total = selectedCategory.tests.reduce(
        (prev: number, curr: string) => {
          const testData: any = Object.values(availableTestsMap).find(
            (test: any) => test?.parentId === curr
          );
          prev += parseInt(testData?.fee) ?? 0;
          return prev ?? 0;
        },
        0
      );
      setValue("fee", total);
      setTotalFee(total);
    }

    // eslint-disable-next-line
  }, [selectedCategory]);

  useEffect(() => {
    setValue("fee", totalFee);
  }, [totalFee]);

  useEffect(() => {
    let feeWithDiscount = totalFee;
    if (initialDiscount.discount) {
      feeWithDiscount =
        +categoryFee - (initialDiscount.discount / 100) * +categoryFee;
    } else if (initialDiscount.flatDiscount) {
      feeWithDiscount = +categoryFee - initialDiscount.flatDiscount;
    }
    setValue("fee", feeWithDiscount);

    // eslint-disable-next-line
  }, [initialDiscount]);

  async function onSubmit(values: any) {
    const tests = values.tests ?? [];
    let fee = parseInt(values.fee);
    try {
      if (!tests?.length) {
        toast({
          status: "error",
          title: "Test Required",
          description: "Please select a test to proceed",
          duration: 3000,
          isClosable: true,
        });
        return;
      }

      const noParentOfTests = tests.filter((test: string) => {
        const parentTest = testsQuery?.data?.find((parentTest: any) => {
          return parentTest.id === test;
        });
        if (!parentTest) return true;
        return false;
      });
      if (noParentOfTests.length) {
        toast({
          status: "error",
          title: "Test Required",
          description: `Corresponding Test not found with the selected test`,
          duration: 3000,
          isClosable: true,
        });
        return;
      }
      if (selectedCategory.id) {
        const editingCategory = {
          ...selectedCategory,
          ...values,
          file: image,
          fee,
        };
        await editTestCategory(editingCategory);
      } else {
        const addingCategory = {
          categoryData: {
            ...values,
            file: image,
            fee,
          },
          initialDiscount,
        };
        await addTestCategory(addingCategory);
      }
      setCategory(undefined);
      setSelectedCategory(defaultCategory);
      onCancel();
    } catch (e) {
      toast({
        status: "error",
        title: "Error !!",
        description: e.message,
        duration: 3000,
        isClosable: true,
      });
    }
  }

  function fileSelected(event: any) {
    if (event.target.files.length !== 0) {
      let img = event.target?.files;
      let imgs: any = [];
      if (img) {
        for (var i = 0; i < img?.length; i++) {
          imgs.push({
            img: img[i],
            url: URL.createObjectURL(img[i]),
          });
        }
      }
      setImage(imgs);
    }
  }

  function openImagePreview(src: any) {
    setImageSrc(src);
    imageModal.onOpen();
  }

  function removeDiscount() {
    setInitialDiscount(new Discount({}));
  }

  function deleteCategoryIcon() {
    setSelectedCategory({ ...selectedCategory, iconUrl: "" });
  }

  const cantAddDiscount = !categoryFee || categoryFee === "0";
  const tooltipDisplay = cantAddDiscount ? "auto" : "none";

  return (
    <Flex direction="column" padding={edit ? "8" : "0"}>
      <Modal
        isOpen={imageModal.isOpen}
        onClose={() => {
          imageModal?.onClose();
        }}
      >
        <ModalOverlay />
        <ModalContent
          onClick={imageModal.onClose}
          boxShadow="none"
          height="80vh"
          bg="rgba(0,0,0,0.4)"
        >
          <Flex justifyContent="center" alignItems="center" height="100%">
            {imageSrc ? (
              <Image
                src={imageSrc}
                maxH={"80vh"}
                width="auto"
                fallbackSrc={`Category Icon`}
              />
            ) : null}
          </Flex>
        </ModalContent>
      </Modal>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Flex direction="column">
          <Flex>
            <Flex flex={3} direction="column">
              <FormControl>
                <Box padding={1} flex={2}>
                  <Text
                    fontSize="md"
                    paddingBottom={1}
                    color="highlight.500"
                    fontWeight="medium"
                  >
                    Name
                  </Text>
                  <Input
                    type="text"
                    paddingY="6"
                    placeholder="Name of the category"
                    borderRadius="4px"
                    defaultValue={selectedCategory?.name}
                    name="name"
                    ref={register}
                    required
                  />
                </Box>
              </FormControl>
            </Flex>
            <FormControl flex={1} marginLeft={6}>
              <FormLabel
                as="legend"
                fontSize="md"
                color="highlight.500"
                fontWeight="medium"
              >
                Price (Rs)
              </FormLabel>
              <Input
                type="number"
                paddingY="6"
                readOnly
                borderRadius="4px"
                placeholder="Enter fee for the category"
                defaultValue={selectedCategory?.fee ?? 0}
                name="fee"
                ref={register()}
                maxLength={2}
                required
              />
            </FormControl>
          </Flex>
        </Flex>
        <FormControl>
          <Box padding={1} flex={2}>
            <Text
              fontSize="md"
              paddingBottom={1}
              color="highlight.500"
              fontWeight="medium"
            >
              Description
            </Text>
            <Input
              type="text"
              paddingY="6"
              placeholder="Description of the category"
              borderRadius="4px"
              defaultValue={selectedCategory?.description}
              name="description"
              ref={register}
              required
            />
          </Box>
        </FormControl>
        <FormControl>
          <Box padding={1} flex={2}>
            <Text
              fontSize="md"
              paddingBottom={1}
              color="highlight.500"
              fontWeight="medium"
            >
              Precautions
            </Text>
            <Input
              type="text"
              paddingY="6"
              placeholder="Precaution for the category"
              borderRadius="4px"
              defaultValue={selectedCategory?.precautions}
              name="precautions"
              ref={register}
              required
            />
          </Box>
        </FormControl>
        <FormControl>
          <FormLabel htmlFor="document">Test Category Icon</FormLabel>
          <Stack direction="row">
            {selectedCategory?.iconUrl && !image ? (
              <Box position="relative">
                <Image
                  boxSize="150px"
                  borderRadius="md"
                  bg="gray.700"
                  objectFit="cover"
                  cursor="pointer"
                  src={selectedCategory?.iconUrl}
                  onClick={() => openImagePreview(selectedCategory?.iconUrl)}
                  fallbackSrc={""}
                />
                <IconButton
                  p={0}
                  position="absolute"
                  top="0"
                  right="0"
                  size="xs"
                  opacity="0.7"
                  onClick={deleteCategoryIcon}
                  aria-label="Delete icon"
                  icon={<RiDeleteBin6Line />}
                />
              </Box>
            ) : null}

            {image &&
              image.map((img: any, index: any) => (
                <LocalFilePreview
                  key={index}
                  file={img}
                  image={image}
                  setImage={setImage}
                  index={index}
                />
              ))}
          </Stack>
          <input
            ref={(e) => {
              uploadRef.current = e;
            }}
            onChange={fileSelected}
            style={{ display: "none", marginTop: 10 }}
            name="iconUrl"
            placeholder="Select Icon"
            type="file"
            accept="image/*"
          />

          <Button
            mr={3}
            onClick={() => uploadRef.current?.click()}
            h="32px"
            background="white"
            marginTop="3px"
            marginBottom="5px"
            color="black"
            border="1px solid black"
          >
            Upload Icon
          </Button>
        </FormControl>
        <FormControl>
          <FormLabel as="legend">Select Tests</FormLabel>
        </FormControl>
        <ShowLabTests
          availableTestsMap={availableTestsMap}
          selectedCategory={selectedCategory}
          setSelectedCategory={setSelectedCategory}
          errors={errors}
          control={control}
        />

        {initialDiscount?.name && !selectedCategory.id ? (
          <DiscountCard
            key={0}
            discount={initialDiscount}
            editDiscount={() => {}}
            deleteDiscount={removeDiscount}
            showEdit={false}
          ></DiscountCard>
        ) : null}
        <Flex marginY="5">
          {!selectedCategory.id ? (
            <Tooltip
              label="Please enter category details"
              aria-label="A tooltip"
              display={tooltipDisplay}
            >
              <div>
                <Button
                  mr={3}
                  onClick={() => {
                    discountAlert.onOpen();
                  }}
                  disabled={cantAddDiscount}
                >
                  Add Discount
                </Button>
              </div>
            </Tooltip>
          ) : null}
          <Button
            mr={3}
            type="submit"
            isLoading={
              addTestCategoryInfo.isLoading || editTestCategoryInfo.isLoading
            }
          >
            {selectedCategory.name ? "Update Category" : "Submit"}
          </Button>
        </Flex>
      </form>
      {/* This is discount modal */}
      <Modal isOpen={discountAlert.isOpen} onClose={discountAlert.onClose}>
        <ModalOverlay></ModalOverlay>
        <ModalContent bg="white" borderRadius="4px" maxW="50vw">
          <Box>
            <Box>
              <DiscountSection
                initialDiscount={initialDiscount}
                setInitialDiscount={setInitialDiscount}
                discountAlert={discountAlert}
                categoryFee={categoryFee}
              ></DiscountSection>
            </Box>
          </Box>
        </ModalContent>
      </Modal>
    </Flex>
  );
}

function ShowLabTests({
  availableTestsMap,
  selectedCategory,
  setSelectedCategory,
  errors,
  control,
}: any) {
  return (
    <Flex direction="column" style={{ marginBottom: "10px" }}>
      <Controller
        render={(props) => (
          <Grid
            templateColumns="repeat(auto-fit, minmax(150px, 1fr))"
            wrap="wrap"
            gap={1}
          >
            {Object.keys(availableTestsMap).map((test: any, index) => {
              const parentId = availableTestsMap[test]?.parentId;
              let selected: boolean =
                selectedCategory?.tests.includes(parentId) || false;
              return (
                <Box
                  cursor="pointer"
                  padding="2px 4px"
                  border="2px solid"
                  borderColor={selected ? "primary.500" : "border.500"}
                  align="center"
                  borderRadius={4}
                  backgroundColor={selected ? "primary.500" : "white"}
                  color={selected ? "white" : "highlight.500"}
                  index={index}
                  key={index}
                  ref={props.ref}
                  onClick={() => {
                    if (selectedCategory?.tests.includes(parentId)) {
                      selectedCategory.tests = selectedCategory?.tests.filter(
                        (cat: any) => cat !== parentId
                      );
                    } else {
                      selectedCategory?.tests.push(parentId);
                    }
                    props.onChange(selectedCategory?.tests || []);
                    setSelectedCategory({ ...selectedCategory } as Discount);
                  }}
                >
                  <div>{availableTestsMap[test]?.name ?? "Deleted Test"}</div>
                </Box>
              );
            })}
          </Grid>
        )}
        name="tests"
        control={control}
        rules={{
          required: true,
          validate: () => {
            if (selectedCategory?.tests.length === 0) {
              return "At least one test should be selected";
            }
          },
        }}
        defaultValue={selectedCategory?.tests}
      ></Controller>
      <ErrorMessage
        errors={errors}
        name="tests"
        as={<Text color="error.500" padding={1} fontSize="sm"></Text>}
      ></ErrorMessage>
    </Flex>
  );
}

function DiscountSection({
  initialDiscount,
  setInitialDiscount,
  discountAlert,
  categoryFee,
}: any) {
  const { handleSubmit, register, errors, setValue, control, watch } =
    useForm();
  const [isFlatDiscount, setIsFlatDiscount] = useState();

  async function submitDiscount(values: any) {
    values.startTime = getBaseTimeStamp({ date: new Date(values.startTime) });
    if (initialDiscount?.runsForever) {
      values.endTime = add(new Date(), { years: 100 });
    } else {
      values.endTime = new Date(values.endTime);
    }
    values.endTime = getBaseTimeStamp({ date: values.endTime });
    if (isFlatDiscount) {
      values.flatDiscount = parseInt(values.discount);
      values.discount = null;
    } else {
      values.discount = parseInt(values.discount);
      values.flatDiscount = null;
    }
    initialDiscount = {
      ...initialDiscount,
      ...values,
    };
    setInitialDiscount({ ...initialDiscount });
    discountAlert.onClose();
  }

  return (
    <DiscountForm
      initialDiscount={initialDiscount}
      setInitialDiscount={setInitialDiscount}
      submitDiscount={submitDiscount}
      handleSubmit={handleSubmit}
      isFlatDiscount={isFlatDiscount}
      register={register}
      setIsFlatDiscount={setIsFlatDiscount}
      errors={errors}
      setValue={setValue}
      control={control}
      watch={watch}
      discountAlert={discountAlert}
      categoryFee={categoryFee}
    ></DiscountForm>
  );
}

function DiscountForm({
  initialDiscount,
  setInitialDiscount,
  submitDiscount,
  handleSubmit,
  isFlatDiscount,
  register,
  setIsFlatDiscount,
  errors,
  setValue,
  control,
  watch,
  discountAlert,
  categoryFee,
}: any) {
  function showWeekDays() {
    return (
      <Flex direction="column">
        <Controller
          render={(props) => (
            <Grid
              templateColumns="repeat(auto-fit, minmax(100px, 1fr))"
              wrap="wrap"
              gap={3}
            >
              {weekdays.map((weekday: any, index: number) => {
                index = index + 1;
                let selected: boolean =
                  initialDiscount?.weekdays.includes(index) || false;

                return (
                  <Box
                    cursor="pointer"
                    padding="2px 4px"
                    border="2px solid"
                    borderColor={selected ? "primary.500" : "border.500"}
                    align="center"
                    borderRadius={4}
                    backgroundColor={selected ? "primary.500" : "white"}
                    color={selected ? "white" : "highlight.500"}
                    index={index}
                    key={index}
                    ref={props.ref}
                    onClick={() => {
                      if (initialDiscount?.weekdays.includes(index)) {
                        initialDiscount.weekdays =
                          initialDiscount?.weekdays.filter(
                            (cat: any) => cat !== index
                          );
                      } else {
                        initialDiscount?.weekdays.push(index);
                      }
                      props.onChange(initialDiscount?.weekdays || []);
                      setInitialDiscount({ ...initialDiscount } as Discount);
                    }}
                  >
                    <div>{weekday}</div>
                  </Box>
                );
              })}
            </Grid>
          )}
          name="weekdays"
          control={control}
          rules={{
            required: true,
            validate: () => {
              if (initialDiscount?.weekdays.length === 0) {
                return "At least one weekday should be selected";
              }
            },
          }}
          defaultValue={initialDiscount?.weekdays}
        ></Controller>
        <ErrorMessage
          errors={errors}
          name="weekdays"
          as={<Text color={"error"} padding={1} fontSize="sm"></Text>}
        ></ErrorMessage>
      </Flex>
    );
  }

  function changeEndDateTime(runsForever: any) {
    if (runsForever) {
      setValue(
        "endTime",
        getFormattedStringDate(
          add(new Date(), {
            years: 100,
          })
        )
      );
      setInitialDiscount({
        ...initialDiscount,
        runsForever,
        endTime: new Date(
          add(new Date(), {
            years: 100,
          })
        ).getTime(),
      });
    } else {
      setValue(
        "endTime",
        getFormattedStringDate(
          add(new Date(), {
            days: 1,
          })
        )
      );
      setInitialDiscount({
        ...initialDiscount,
        runsForever,
        endTime: new Date(
          add(new Date(), {
            days: 1,
          })
        ).getTime(),
      });
    }
  }
  const watchStartTime = watch("startTime");

  useEffect(() => {
    setValue(
      "discount",
      initialDiscount?.flatDiscount ?? initialDiscount?.discount
    );
  }, []);

  function validateEndTime(value: any) {
    return value > watchStartTime;
  }

  return (
    <div>
      <form onSubmit={handleSubmit(submitDiscount)}>
        {initialDiscount && (
          <Flex direction="column">
            <AlertDialogHeader
              bg="modalHeader.500"
              color="white"
              borderTopRadius="4px"
            >
              Add Discount for the category
            </AlertDialogHeader>
            <AlertDialogCloseButton color="faded.500" fontSize="14" />
            <Stack direction="column" borderRadius={4} padding="8px 16px">
              <Flex justify="space-between">
                <Box padding={1} flex={2}>
                  <Text
                    fontSize="md"
                    paddingBottom={1}
                    color="highlight.500"
                    fontWeight="medium"
                  >
                    Discount Title
                  </Text>
                  <Input
                    type="text"
                    paddingY="6"
                    placeholder="Name of the discount"
                    borderRadius="4px"
                    defaultValue={initialDiscount.name}
                    name="name"
                    ref={register}
                    required
                  />
                </Box>

                <Box padding={1} flex={1} marginLeft={6}>
                  <Flex justify="space-between">
                    <Text
                      fontSize="md"
                      paddingBottom={1}
                      color="highlight.500"
                      fontWeight="medium"
                    >
                      Discount {!isFlatDiscount ? "(In %)" : "(In Rs)"}
                    </Text>
                    <Checkbox
                      paddingBottom={1}
                      color="highlight.500"
                      fontSize="md"
                      colorScheme="primary"
                      defaultChecked={initialDiscount.flatDiscount}
                      checked={initialDiscount?.flatDiscount}
                      onChange={(e) => setIsFlatDiscount(e.target.checked)}
                    >
                      Flat Discount
                    </Checkbox>
                  </Flex>
                  <Input
                    type="number"
                    paddingY="6"
                    borderRadius="4px"
                    placeholder="Enter discount"
                    defaultValue={initialDiscount.discount}
                    max={!isFlatDiscount ? "90" : categoryFee} //This max is the fee of selectedCategory
                    name="discount"
                    ref={register}
                    maxLength={
                      !isFlatDiscount ? 2 : categoryFee?.toString().length
                    }
                    required
                  />
                </Box>
              </Flex>
              <Flex direction="column">
                <Flex direction="column">
                  <Text
                    fontSize="sm"
                    paddingBottom={1}
                    color={"highlight"}
                    fontWeight="medium"
                  >
                    Select Days
                  </Text>
                  {showWeekDays()}
                </Flex>

                <Flex align="center" justify="space-between" marginY={2}>
                  <Flex direction="column" justify="center" flex={1}>
                    <Text
                      fontSize="md"
                      color={"highlight"}
                      fontWeight="medium"
                      marginY="1"
                    >
                      From
                    </Text>
                    <Input
                      paddingY="6"
                      min={getFormattedStringDate(
                        new Date(new Date().getTime())
                      )}
                      type="date"
                      defaultValue={getFormattedStringDate(
                        new Date(initialDiscount.startTime)
                      )}
                      placeholder="Start Date"
                      onChange={(e) =>
                        setInitialDiscount({
                          ...initialDiscount,
                          startTime: new Date(e.target.value),
                        })
                      }
                      name="startTime"
                      ref={register}
                      required
                    />
                  </Flex>
                  <Box margin={2}></Box>
                  <Flex direction="column" flex={1} justify="center">
                    <Flex justify="space-between">
                      <Text
                        fontSize="md"
                        color="#000"
                        fontWeight="medium"
                        marginY="1"
                      >
                        To
                      </Text>
                      <Checkbox
                        pt="2"
                        colorScheme="primary"
                        defaultChecked={initialDiscount?.runsForever}
                        checked={initialDiscount?.runsForever}
                        onChange={(e) => changeEndDateTime(e.target.checked)}
                      >
                        Runs Forever
                      </Checkbox>
                    </Flex>
                    <Input
                      paddingY="6"
                      ref={register({
                        validate: validateEndTime,
                      })}
                      min={getFormattedStringDate(
                        add(new Date(initialDiscount.startTime), {
                          days: 1,
                        })
                      )}
                      type="date"
                      placeholder="End Date"
                      name="endTime"
                      defaultValue={getFormattedStringDate(
                        new Date(initialDiscount.endTime)
                      )}
                      disabled={initialDiscount?.runsForever}
                      required
                    />
                  </Flex>
                </Flex>
              </Flex>
              <Flex>
                <Button type="submit" isLoading={false} margin="2" paddingY="5">
                  Add Discount
                </Button>
                <Button
                  margin="2"
                  onClick={() => {
                    discountAlert.onClose?.(initialDiscount);
                  }}
                  background="white"
                  color="highlight.500"
                  border="solid 2px"
                  borderColor="faded.500"
                  paddingY="5"
                >
                  Cancel
                </Button>
              </Flex>
            </Stack>
          </Flex>
        )}
      </form>
    </div>
  );
}
